import { Image } from 'expo-image';
import { Link, type Href } from 'expo-router';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Pressable, Text, View } from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { coverUri } from '@/api/client';
import { clothOf } from '@/components/BookCover';
import { Mascot } from '@/components/Mascot';
import { useLift } from '@/components/ui/motion';
import { TextButton } from '@/components/ui/TextButton';
import { useTheme, type ShelfMaterial } from '@/theme';

/** A book standing on a shelf. `label` is what a screen reader says for it. */
export interface ShelfBook {
  key: string;
  href: Href;
  title: string;
  author: string;
  cover: string | null;
  pages: number | null;
  label: string;
}

/** Books stand this far in from the ends of the board. */
const INSET = 8;
const COVER_GAP = 10;
/** Books sink this far into the board's lit top, so they stand on it rather than float above it. */
const CONTACT = 2;
/** A long shelf shows this many rows until you ask for the rest. */
const ROWS_SHOWN = 3;

/** A steady 0..1 per title, so a book keeps its size and thickness from visit to visit. */
function steady(title: string, seed = 7): number {
  let h = seed;
  for (let i = 0; i < title.length; i++) h = (h * 31 + title.charCodeAt(i)) >>> 0;
  return (h % 1000) / 1000;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) rows.push(items.slice(i, i + size));
  return rows;
}

/** How many covers fit a row, and how wide each is so the row fills the board. */
function coverColumns(width: number, large: boolean) {
  const target = large ? (width >= 700 ? 128 : 96) : width >= 700 ? 96 : 72;
  const inner = width - 2 * INSET;
  const perRow = Math.max(2, Math.floor((inner + COVER_GAP) / (target + COVER_GAP)));
  return { perRow, bookWidth: Math.floor((inner - (perRow - 1) * COVER_GAP) / perRow) };
}

/** A spine: as thick as its page count suggests (never too thin to tap), and a steady height. */
function spineOf(book: ShelfBook) {
  const thickness = Math.round(Math.max(28, Math.min(48, book.pages ? 18 + book.pages / 22 : 26 + steady(book.title) * 12)));
  const height = Math.round(122 + steady(book.title, 13) * 30);
  return { thickness, height };
}

function spineRows(books: readonly ShelfBook[], width: number): ShelfBook[][] {
  const inner = width - 2 * INSET;
  const rows: ShelfBook[][] = [];
  let row: ShelfBook[] = [];
  let used = 0;
  for (const book of books) {
    const { thickness } = spineOf(book);
    if (used + thickness > inner && row.length > 0) {
      rows.push(row);
      row = [];
      used = 0;
    }
    row.push(book);
    used += thickness + 1;
  }
  if (row.length > 0) rows.push(row);
  return rows;
}

/**
 * A shelf board fixed to the wall (the page): lit along its top, shaded along its lower edge, with grain if
 * it's wood, and a soft shadow on the wall below. How thick and how round is up to the style.
 */
export function Plank({ material, thickness, radius }: { material: ShelfMaterial; thickness: number; radius: number }) {
  const { colors } = useTheme();
  const top = Math.max(2, Math.round(thickness * 0.25));
  return (
    <View aria-hidden style={{ height: thickness, borderRadius: radius, boxShadow: colors.deskShadow }}>
      <View style={{ flex: 1, borderRadius: radius, overflow: 'hidden', backgroundColor: material.face }}>
        {material.grain && (
          <Svg width="100%" height="100%" viewBox="0 0 400 12" preserveAspectRatio="none" style={{ position: 'absolute' }}>
            <Path d="M0 5 C70 3 130 7 210 5 S340 3 400 6" stroke={material.grain} strokeWidth={0.5} strokeOpacity={0.55} fill="none" />
            <Path d="M0 8 C90 10 150 6 240 8 S350 10 400 8" stroke={material.grain} strokeWidth={0.4} strokeOpacity={0.4} fill="none" />
          </Svg>
        )}
        <View style={{ height: top, backgroundColor: material.top }} />
        <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: Math.max(1, Math.round(thickness * 0.17)), backgroundColor: material.edge }} />
      </View>
    </View>
  );
}

/** A book facing out: the cover (or a cloth-bound stand-in with its title), with the crease of its hinge. */
function CoverBook({ book, width }: { book: ShelfBook; width: number }) {
  const { colors, fonts, shelf } = useTheme();
  const lift = useLift();
  // Remembers a failed load for this key only; another cover (or the next visit) tries again.
  const [failedKey, setFailedKey] = useState<string | null>(null);
  const showCover = book.cover !== null && failedKey !== book.cover;
  // Not every book is the same height.
  const height = Math.round(width * 1.5 * (0.94 + 0.06 * steady(book.title)));
  const small = width < 90;
  return (
    <Animated.View style={lift.style}>
      <Link href={book.href} asChild>
        <Pressable accessibilityRole="link" accessibilityLabel={book.label} onPressIn={lift.onPressIn} onPressOut={lift.onPressOut} onHoverIn={lift.onHoverIn} onHoverOut={lift.onHoverOut}>
          <View style={{ width, height, borderRadius: shelf.bookRadius, boxShadow: colors.coverShadow, backgroundColor: clothOf(book.title, colors.people) }}>
            <View style={{ flex: 1, borderRadius: shelf.bookRadius, overflow: 'hidden' }}>
              {showCover ? (
                // Decorative: the link says which book. expo-image drops an empty alt on the web, so the wrapper hides it.
                <View aria-hidden style={{ width, height }}>
                  <Image source={{ uri: coverUri(book.cover!) }} accessibilityIgnoresInvertColors alt="" onError={() => setFailedKey(book.cover)} contentFit="cover" transition={150} style={{ width, height }} />
                </View>
              ) : (
                <View aria-hidden style={{ flex: 1, paddingVertical: 8, paddingLeft: 10, paddingRight: 6, justifyContent: 'space-between' }}>
                  <Text numberOfLines={small ? 4 : 5} style={{ color: '#FFFFFF', fontFamily: fonts.headingBold, fontSize: small ? 11 : 14, lineHeight: small ? 14 : 18 }}>
                    {book.title}
                  </Text>
                  <Text numberOfLines={2} style={{ color: '#FFFFFF', fontSize: small ? 9 : 11, lineHeight: small ? 11 : 14 }}>
                    {book.author}
                  </Text>
                </View>
              )}
              {/* The crease where the cover hinges. */}
              <View aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, backgroundColor: 'rgba(0,0,0,0.16)' }} />
              <View aria-hidden style={{ position: 'absolute', left: 3, top: 0, bottom: 0, width: 1, backgroundColor: 'rgba(255,255,255,0.22)' }} />
            </View>
          </View>
        </Pressable>
      </Link>
    </Animated.View>
  );
}

/** A book spine out: cloth-bound (we don't know its real colour), with its title running down the spine. */
function SpineBook({ book, leaning }: { book: ShelfBook; leaning: boolean }) {
  const { colors, fonts, shelf } = useTheme();
  const lift = useLift();
  const { thickness, height } = spineOf(book);
  const length = height - 26;
  const round = Math.min(shelf.bookRadius, 4);
  // The last book of a row tips against its neighbour, pivoting on its lower corner.
  const lean = leaning ? { marginLeft: Math.round(height * 0.087), transformOrigin: 'left bottom' as const, transform: [{ rotate: '-5deg' }] } : null;
  return (
    <View style={lean}>
      <Animated.View style={lift.style}>
        <Link href={book.href} asChild>
          <Pressable accessibilityRole="link" accessibilityLabel={book.label} onPressIn={lift.onPressIn} onPressOut={lift.onPressOut} onHoverIn={lift.onHoverIn} onHoverOut={lift.onHoverOut}>
            <View style={{ width: thickness, height, borderTopLeftRadius: round, borderTopRightRadius: round, overflow: 'hidden', backgroundColor: clothOf(book.title, colors.people) }}>
              {/* Light on one side of the rounded spine, shade on the other; a band at the head and foot. */}
              <View aria-hidden style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 2, backgroundColor: 'rgba(255,255,255,0.16)' }} />
              <View aria-hidden style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 3, backgroundColor: 'rgba(0,0,0,0.2)' }} />
              <View aria-hidden style={{ position: 'absolute', left: 0, right: 0, top: 7, height: 1, backgroundColor: 'rgba(255,255,255,0.4)' }} />
              <View aria-hidden style={{ position: 'absolute', left: 0, right: 0, bottom: 7, height: 1, backgroundColor: 'rgba(255,255,255,0.4)' }} />
              <View
                aria-hidden
                style={{
                  position: 'absolute',
                  width: length,
                  height: thickness,
                  left: (thickness - length) / 2,
                  top: (height - thickness) / 2,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  transform: [{ rotate: '90deg' }],
                }}
              >
                <Text numberOfLines={1} style={{ flex: 1, color: '#FFFFFF', fontFamily: fonts.heading, fontSize: 12 }}>
                  {book.title}
                </Text>
              </View>
            </View>
          </Pressable>
        </Link>
      </Animated.View>
    </View>
  );
}

/**
 * One shelf of your bookcase: a heading, then books standing on boards, as many boards as the books need.
 * Face out, or spine out. `caption` adds something under each book, below the board (Reading now: your place
 * and Update). Empty, it's a bare board with a line on what goes there.
 */
export function Shelf({
  title,
  books,
  layout = 'covers',
  large = false,
  material,
  empty,
  caption,
  bookend = false,
}: {
  title: string;
  books: readonly ShelfBook[];
  layout?: 'covers' | 'spines';
  large?: boolean;
  material: ShelfMaterial;
  empty?: ReactNode;
  caption?: (key: string) => ReactNode;
  /** Playful: the bunny sits at the end of the last row, if there's room. */
  bookend?: boolean;
}) {
  const { t } = useTranslation();
  const { art, colors, fonts, fontSize, shelf, space } = useTheme();
  const [width, setWidth] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const covers = coverColumns(width || 343, large);
  const rows = width === 0 ? [] : layout === 'spines' ? spineRows(books, width) : chunk(books, covers.perRow);
  const shown = showAll ? rows : rows.slice(0, ROWS_SHOWN);
  const lastRow = rows[rows.length - 1];
  const hasRoom =
    lastRow !== undefined && (layout === 'spines' ? lastRow.reduce((sum, b) => sum + spineOf(b).thickness + 1, 0) + 96 < width - 2 * INSET : lastRow.length < covers.perRow);
  // As a list only when it's nothing but books: controls beside them don't belong in one.
  const asList = !caption;

  return (
    <View style={{ gap: space.md }}>
      <Text accessibilityRole="header" aria-level={2} style={{ fontFamily: fonts.heading, fontSize: fontSize.lg, lineHeight: fontSize.lg * 1.25, color: colors.text }}>
        {title}
        {books.length > 0 && <Text style={{ fontFamily: fonts.reading, fontSize: fontSize.md, color: colors.textMuted }}>{`  ${books.length}`}</Text>}
      </Text>
      <View role={asList && books.length > 0 ? 'list' : undefined} aria-label={asList && books.length > 0 ? title : undefined} onLayout={(e) => setWidth(e.nativeEvent.layout.width)} style={{ gap: space.xl }}>
        {books.length === 0 ? (
          <View>
            <View style={{ minHeight: 56, justifyContent: 'flex-end', paddingHorizontal: INSET, paddingBottom: space.sm, gap: space.sm }}>{empty}</View>
            <Plank material={material} thickness={shelf.thickness} radius={shelf.radius} />
          </View>
        ) : (
          shown.map((row, i) => {
            const isLast = i === rows.length - 1;
            const partial = isLast && hasRoom;
            return (
              <View key={row[0]!.key}>
                <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: layout === 'spines' ? 1 : COVER_GAP, paddingHorizontal: INSET, marginBottom: -CONTACT, zIndex: 1 }}>
                  {row.map((book, j) => (
                    <View key={book.key} role={asList ? 'listitem' : undefined}>
                      {layout === 'spines' ? <SpineBook book={book} leaning={partial && j === row.length - 1 && row.length > 1} /> : <CoverBook book={book} width={covers.bookWidth} />}
                    </View>
                  ))}
                  {bookend && art && partial && (
                    <View style={{ flex: 1, alignItems: 'flex-end' }}>
                      <Mascot pose="reading" bare size={Math.min(covers.bookWidth, 84)} style={{ alignSelf: 'flex-end', marginBottom: CONTACT }} />
                    </View>
                  )}
                </View>
                <Plank material={material} thickness={shelf.thickness} radius={shelf.radius} />
                {caption && (
                  <View style={{ flexDirection: 'row', gap: COVER_GAP, paddingHorizontal: INSET, paddingTop: space.md }}>
                    {row.map((book) => (
                      <View key={book.key} style={{ width: covers.bookWidth }}>
                        {caption(book.key)}
                      </View>
                    ))}
                  </View>
                )}
              </View>
            );
          })
        )}
      </View>
      {rows.length > ROWS_SHOWN && (
        <View style={{ alignSelf: 'flex-start' }}>
          <TextButton label={showAll ? t('myBooks.showLess') : t('myBooks.showAll', { count: books.length })} onPress={() => setShowAll((s) => !s)} />
        </View>
      )}
    </View>
  );
}
